import { coerceTrackingScalar } from '../configurable-step/resolveElementFields';
import { getByPath } from '../interpolation/getByPath';
import type { PathSegment } from '../interpolation/types';
import type { BindingExperience, BindingFieldSource } from './types';

export interface BindingResolveScope {
  readonly item?: unknown;
  readonly sources: Readonly<Record<string, unknown>>;
  readonly experience: BindingExperience;
}

const EXPERIENCE_KEYS = new Set<keyof BindingExperience>([
  'dslConfigId',
  'dslConfigVersion',
  'flowId',
  'stepId',
  'stepIndex',
  'blockId',
  'blockType',
  'pageCategory',
  'contentGroup',
]);

/**
 * Plain dot path, matching a visibility `when.path`: key segments only.
 * `$`, wildcards, brackets, and empty segments do not resolve.
 */
function bindingSegments(path: string): readonly PathSegment[] | undefined {
  if (
    path.length === 0 ||
    path.includes('$') ||
    path.includes('*') ||
    path.includes('[') ||
    path.includes(']')
  ) {
    return undefined;
  }
  const names = path.split('.');
  if (names.some((name) => name.trim().length === 0)) return undefined;
  return names.map((name) => ({ type: 'key', name: name.trim() }));
}

function scalarAt(
  root: unknown,
  segments: readonly PathSegment[],
): string | undefined {
  const value = segments.length === 0 ? root : getByPath(root, segments);
  if (value === null || typeof value === 'object') return undefined;
  const coerced = coerceTrackingScalar(value);
  if (coerced === undefined || coerced.length === 0) return undefined;
  return coerced;
}

const VISITOR_PATH_NAMES = new Set([
  'postalcode',
  'stateabbr',
  'ipaddress',
  'ipinferredpostalcode',
]);

/**
 * These names also appear on a school location and a program start
 * date. They redact only when the path has a `lead` segment.
 */
const LEAD_PATH_NAMES = new Set(['address', 'city', 'state', 'startdate']);

/**
 * Lead-field names on a binding path. A non-empty `item.*` or
 * `source.*` value whose path names one of these is replaced with
 * `SANITIZED` before it can leave on a configured event. A value
 * whose path does not say what it is stays as resolved.
 */
function pathCarriesPii(segments: readonly PathSegment[]): boolean {
  const names = segments.flatMap((segment) =>
    segment.type === 'key'
      ? [segment.name.toLowerCase().replace(/[^a-z0-9]/g, '')]
      : [],
  );
  const underLead = names.includes('lead');
  return names.some(
    (name) =>
      VISITOR_PATH_NAMES.has(name) ||
      name.includes('firstname') ||
      name.includes('lastname') ||
      name.includes('email') ||
      name.includes('phonenumber') ||
      name.includes('phone') ||
      name.includes('password') ||
      (underLead && (LEAD_PATH_NAMES.has(name) || name.includes('address'))),
  );
}

function redactVisitorValue(
  segments: readonly PathSegment[],
  value: string | undefined,
): string | undefined {
  if (value === undefined || !pathCarriesPii(segments)) return value;
  return 'SANITIZED';
}

/**
 * Resolve one binding field to a string. `undefined` means the field
 * missed: a non-scalar, an empty string, an undeclared source, `item`
 * outside a repeater, or an unknown root or experience key.
 */
export function resolveBindingField(
  source: BindingFieldSource,
  scope: BindingResolveScope,
): string | undefined {
  if ('literal' in source && typeof source.literal === 'string') {
    return source.literal.length > 0 ? source.literal : undefined;
  }

  const segments = bindingSegments(source.dataPath);
  if (!segments || segments.length === 0) return undefined;
  const [root, ...rest] = segments;
  if (!root || root.type !== 'key') return undefined;

  if (root.name === 'item') {
    if (scope.item === undefined) return undefined;
    return redactVisitorValue(segments, scalarAt(scope.item, rest));
  }
  if (root.name === 'source') {
    const key = rest[0];
    if (!key || key.type !== 'key' || !Object.hasOwn(scope.sources, key.name)) {
      return undefined;
    }
    return redactVisitorValue(
      segments,
      scalarAt(scope.sources[key.name], rest.slice(1)),
    );
  }
  if (root.name === 'experience') {
    const key = rest[0];
    if (
      !key ||
      key.type !== 'key' ||
      !EXPERIENCE_KEYS.has(key.name as keyof BindingExperience) ||
      !Object.hasOwn(scope.experience, key.name)
    ) {
      return undefined;
    }
    return scalarAt(scope.experience, rest);
  }
  return undefined;
}
